import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Grid,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Divider,
  Badge,
  Avatar,
  CircularProgress,
} from "@mui/material";
import {
  NotificationsActive as ActiveIcon,
  NotificationsOff as SuppressedIcon,
  DeleteOutline as DeletedIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CompletedIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { CrmMainService } from "../../../service/manage/crm/crm_main.service";
import CrmDrawer from "./CrmDrawer";
import sweetAlert, {
  sweetConfirmAlert,
  sweetStatusAlert,
} from "../../../components/common/SweetAlert";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { forwardRef } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useEffect } from "react";

const STATUS = {
  ACTIVE: "1",
  SUPPRESSED: "2",
  DELETED: "3",
  COMPLETED: "4",
};

const STATUS_CONFIG = {
  [STATUS.ACTIVE]: {
    label: "Active",
    color: "success",
    icon: <ActiveIcon />,
  },
  [STATUS.SUPPRESSED]: {
    label: "Suppressed",
    color: "warning",
    icon: <SuppressedIcon />,
  },
  [STATUS.DELETED]: {
    label: "Deleted",
    color: "error",
    icon: <DeletedIcon />,
  },
  [STATUS.COMPLETED]: {
    label: "Completed",
    color: "primary",
    icon: <CompletedIcon />,
  },
};

const STATUS_SEVERITY_MAP = {
  [STATUS.ACTIVE]: "success",
  [STATUS.SUPPRESSED]: "warning",
  [STATUS.DELETED]: "error",
};

const ReminderModal = ({
  open,
  onClose,
  reminders,
  state,
  fetchAllReminders,
  onCloseDrawer,
  handleSearchFilter,
  handleNavigate,
  handleFilterForLinkedBlocks,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [statusUpdates, setStatusUpdates] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingStatus, setLoadingStatus] = useState({
    id: null,
    targetStatus: null,
  });
  const [notificationCount, setNotificationCount] = useState(0);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  useEffect(() => {
    if (reminders.length > 0) {
      const count = reminders.filter(
        (reminder) => reminder.remninder_status === "1"
      ).length;
      setNotificationCount(count);
    } else {
      setNotificationCount(0);
    }
  }, [reminders]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleStatusChange = async (reminderId, newStatus) => {
    try {
      const reminder = reminders.find((r) => r.reminder_id === reminderId);
      if (!reminder || reminder.remninder_status === newStatus) return;

      setLoadingStatus({ id: reminderId, targetStatus: newStatus });

      const updateReminder = {
        node_mid: state?.mid,
        user_id: state?.lg_user_id,
        user_table_id: state?.lg_user_table_id,
        block_id: reminder.block_id,
        reminder_id: reminder.reminder_id,
        reminder_status: newStatus,
      };

      const response = await CrmMainService.crm_update_reminder(updateReminder);

      if (response.success) {
        handleMenuClose();
        setSnackbar({
          open: true,
          message: `Reminder status changed to ${STATUS_CONFIG[newStatus].label} successfully!`,
          severity: STATUS_SEVERITY_MAP[newStatus] || "success",
        });

        if (fetchAllReminders) {
          await fetchAllReminders();
        }
      } else {
        setSnackbar({
          open: true,
          message:
            response.message.error ||
            "Failed to update reminder status. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error updating reminder status:", error);
      setSnackbar({
        open: true,
        message: "Failed to update reminder status. Please try again.",
        severity: "error",
      });
    } finally {
      setLoadingStatus({ id: null, targetStatus: null });
      handleMenuClose();
    }
  };

  const handleReminderClick = async (reminder) => {
    onClose();

    const linkedBlocksArray = [
      {
        block_id: reminder?.block_id,
        comment: reminder?.block_title_text,
        displayOrder: reminder?.display_order,
      },
    ];
    handleFilterForLinkedBlocks(linkedBlocksArray);
  };

  const handleMenuOpen = (event, reminder) => {
    setAnchorEl(event.currentTarget);
    setSelectedReminder(reminder);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReminder(null);
  };

  const formatReminderDate = (dateString) => {
    if (!dateString) return "No date";

    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);
    const hour = dateString.substring(8, 10);
    const minute = dateString.substring(10, 12);

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const formattedHour = hour > 12 ? hour - 12 : hour;
    const amPm = hour >= 12 ? "PM" : "AM";

    return `${monthNames[parseInt(month) - 1]} ${parseInt(
      day
    )}, ${year} ${formattedHour}:${minute}${amPm}`;
  };

  const filteredReminders = reminders.filter((reminder) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      reminder.block_title_text.toLowerCase().includes(searchLower) ||
      reminder.reminder_text_long.toLowerCase().includes(searchLower)
    );
  });

  const getReminderTypeText = (type) => {
    switch (type) {
      case "1":
        return "Assignee";
      case "2":
        return "Creator";
      case "3":
        return "Creator & Assignee";
      default:
        return "Unknown";
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 700 },
          maxHeight: "85vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 3,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ p: 3, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
          <Typography variant="h5" fontWeight="bold" mb={1}>
            Your Reminders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredReminders.length}{" "}
            {filteredReminders.length === 1 ? "reminder" : "reminders"} (
            {notificationCount} active)
          </Typography>
        </Box>

        {/* <Box sx={{ p: 2 }}>
          <TextField
            placeholder="Search reminders..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{ mb: 2 }}
          />
        </Box> */}

        <Box sx={{ overflowY: "auto", flex: 1, p: 2 }}>
          <Grid container spacing={2}>
            {filteredReminders.map((reminder) => {
              const currentStatus =
                statusUpdates[reminder.reminder_id] ||
                reminder.remninder_status;
              const statusConfig =
                STATUS_CONFIG[currentStatus] || STATUS_CONFIG[STATUS.ACTIVE];

              return (
                <Grid item xs={12} key={reminder.reminder_id}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 3,
                      },
                      borderLeft: `4px solid ${statusConfig.color}.main`,
                    }}
                    onClick={() => handleReminderClick(reminder)}
                  >
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      badgeContent={
                        <Avatar
                          sx={{
                            bgcolor: `${statusConfig.color}.main`,
                            width: 24,
                            height: 24,
                          }}
                        >
                          {React.cloneElement(statusConfig.icon, {
                            sx: { fontSize: 16 },
                          })}
                        </Avatar>
                      }
                    >
                      <Avatar
                        sx={{
                          bgcolor: "background.default",
                          width: 40,
                          height: 40,
                          color: "text.secondary",
                        }}
                      >
                        <ActiveIcon />
                      </Avatar>
                    </Badge>

                    <Box
                      sx={{
                        flex: 1,
                        ml: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight="medium"
                        noWrap
                      >
                        {reminder.block_title_text}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {reminder.reminder_text_long}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          label={statusConfig.label}
                          size="small"
                          color={statusConfig.color}
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                        <Chip
                          label={formatReminderDate(
                            reminder.reminder_id.toString()
                          )}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                        <Chip
                          label={getReminderTypeText(reminder.reminder_type)}
                          size="small"
                          variant="outlined"
                          color="info"
                        />
                      </Box>
                    </Box>

                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, reminder);
                      }}
                      sx={{ ml: 1 }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Status change menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() =>
              handleStatusChange(selectedReminder?.reminder_id, STATUS.ACTIVE)
            }
            sx={{
              color: "success.main",
              bgcolor:
                selectedReminder?.remninder_status === STATUS.ACTIVE
                  ? "action.selected"
                  : "inherit",
            }}
            disabled={
              selectedReminder?.remninder_status === STATUS.ACTIVE ||
              (loadingStatus.id === selectedReminder?.reminder_id &&
                loadingStatus.targetStatus !== STATUS.ACTIVE)
            }
          >
            {loadingStatus.id === selectedReminder?.reminder_id &&
            loadingStatus.targetStatus === STATUS.ACTIVE ? (
              <CircularProgress size={20} sx={{ mr: 1.5 }} />
            ) : (
              <ActiveIcon sx={{ mr: 1.5 }} />
            )}
            Set as Active
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleStatusChange(
                selectedReminder?.reminder_id,
                STATUS.SUPPRESSED
              )
            }
            sx={{
              color: "warning.main",
              bgcolor:
                selectedReminder?.remninder_status === STATUS.SUPPRESSED
                  ? "action.selected"
                  : "inherit",
            }}
            disabled={
              selectedReminder?.remninder_status === STATUS.SUPPRESSED ||
              (loadingStatus.id === selectedReminder?.reminder_id &&
                loadingStatus.targetStatus !== STATUS.SUPPRESSED)
            }
          >
            {loadingStatus.id === selectedReminder?.reminder_id &&
            loadingStatus.targetStatus === STATUS.SUPPRESSED ? (
              <CircularProgress size={20} sx={{ mr: 1.5 }} />
            ) : (
              <SuppressedIcon sx={{ mr: 1.5 }} />
            )}
            Suppress Reminder
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() =>
              handleStatusChange(selectedReminder?.reminder_id, STATUS.DELETED)
            }
            sx={{
              color: "error.main",
              bgcolor:
                selectedReminder?.remninder_status === STATUS.DELETED
                  ? "action.selected"
                  : "inherit",
            }}
            disabled={
              selectedReminder?.remninder_status === STATUS.DELETED ||
              (loadingStatus.id === selectedReminder?.reminder_id &&
                loadingStatus.targetStatus !== STATUS.DELETED)
            }
          >
            {loadingStatus.id === selectedReminder?.reminder_id &&
            loadingStatus.targetStatus === STATUS.DELETED ? (
              <CircularProgress size={20} sx={{ mr: 1.5 }} />
            ) : (
              <DeletedIcon sx={{ mr: 1.5 }} />
            )}
            Delete Reminder
          </MenuItem>
        </Menu>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000} // Changed to 3 seconds (3000ms)
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            variant="filled"
            sx={{
              width: "100%",
              alignItems: "center",
              "& .MuiAlert-message": {
                flexGrow: 1,
              },
            }}
            iconMapping={{
              success: <CheckCircleOutlineIcon fontSize="inherit" />,
              warning: <WarningAmberOutlinedIcon fontSize="inherit" />,
              error: <ErrorOutlineOutlinedIcon fontSize="inherit" />,
              info: <InfoOutlinedIcon fontSize="inherit" />,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {snackbar.message}
            </Box>
          </Alert>
        </Snackbar>
      </Box>
    </Modal>
  );
};

export default ReminderModal;
